import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import useN2hSuggestions, {
  MIN_TEXT_CHARS,
} from "../../../hooks/useN2hSuggestions";
import { getNiceToHaves } from "../../../services/commonServices";
import { formatResponse } from "../../../helpers/common";
import { BUY } from "../../../constants/common";
import "./AiNiceToHaveSuggestions.scss";

// Backend tabs are lowercase; the form's nice2Have rows match on these exact
// category keys — all 8 BUY form sections.
const TAB_MAP = {
  property: "Property",
  layout: "Layout",
  exterior: "Exterior",
  interior: "Interior",
  other: "Other",
  architectural_style: "Architectural_Style",
  other_structures: "Other_Structures",
  association_amenities: "Association_Amenities",
};
const MAX_NICE_TO_HAVES = 25;

const rowKey = (tab, id) => `${tab}:${Number(id)}`;

// Auto-ticks AI-inferred nice-to-haves into the draft (the agent stays in
// control: every one is announced in a strip and removable there or in the
// selects). A key the agent removed is never re-added — appliedKeys remembers
// everything this session already auto-ticked. aiOwned tracks the subset WE
// ticked that the agent hasn't touched: when a new verdict for the current
// text no longer supports one of those ("no pool" after "pool"), it is
// un-ticked again. Agent-picked keys are never aiOwned, so never un-ticked.
const AiNiceToHaveSuggestions = ({
  adTitle,
  adDesc,
  adType,
  propertyType,
  selectedNiceToHave = [],
  handleFormValues,
}) => {
  const active = (adType || BUY) === BUY && Number(propertyType) !== 6;
  const { suggestions, loading, answered, debouncing } = useN2hSuggestions(
    adTitle,
    adDesc,
    active,
  );
  const textReady =
    `${adTitle || ""}${adDesc || ""}`.trim().length >= MIN_TEXT_CHARS;

  // The MultiValue chips read Icon/Img off the stored row, so injected rows
  // must carry the same master-list fields a dropdown pick would.
  const { data: masterData } = useQuery({
    queryKey: ["niceToHavesMaster", BUY],
    queryFn: () => getNiceToHaves(BUY),
    enabled: active,
    staleTime: Infinity,
    gcTime: 60 * 60 * 1000,
    retry: false,
  });
  const masterMap = useMemo(() => {
    const map = new Map();
    const result = masterData?.result;
    if (result) {
      Object.keys(result).forEach((category) => {
        (formatResponse(result[category]) || []).forEach((opt) => {
          if (opt && opt.value != null) {
            map.set(rowKey(category, opt.value), opt);
          }
        });
      });
    }
    return map;
  }, [masterData]);

  const appliedKeys = useRef({});
  const aiOwned = useRef({});
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Only reconcile on a definitive verdict for the current settled text:
    // never while typing, never on a failed request, never below the minimum.
    if (!active || masterMap.size === 0 || !answered || loading || debouncing)
      return;
    const selected = Array.isArray(selectedNiceToHave)
      ? selectedNiceToHave
      : [];

    const suggestedKeys = new Set();
    suggestions.forEach((s) => {
      const tab = TAB_MAP[s.tab];
      if (tab) suggestedKeys.add(rowKey(tab, s.id));
    });

    // 1. Un-tick our own earlier adds the new text no longer supports.
    const staleKeys = Object.keys(aiOwned.current).filter(
      (k) => !suggestedKeys.has(k),
    );
    let merged = selected;
    let removedAny = false;
    if (staleKeys.length) {
      const staleSet = new Set(staleKeys);
      merged = selected.filter(
        (item) => !staleSet.has(rowKey(item.tab, item.value ?? item.id)),
      );
      removedAny = merged.length !== selected.length;
      staleKeys.forEach((k) => {
        delete aiOwned.current[k];
        // Re-open the key ONLY if we just removed it ourselves; a key the
        // agent removed earlier stays closed and is never re-added.
        const wasStillTicked = selected.some(
          (item) => rowKey(item.tab, item.value ?? item.id) === k,
        );
        if (wasStillTicked) delete appliedKeys.current[k];
      });
    }

    // 2. Tick what the new verdict supports and nothing has claimed yet.
    const fresh = [];
    suggestions.forEach((s) => {
      const tab = TAB_MAP[s.tab];
      if (!tab) return;
      const key = rowKey(tab, s.id);
      if (appliedKeys.current[key]) return;
      const alreadySelected = merged.some(
        (item) => item.tab === tab && Number(item.value ?? item.id) === Number(s.id),
      );
      appliedKeys.current[key] = true;
      if (alreadySelected) return;
      fresh.push({ key, tab, id: Number(s.id), label: s.label, evidence: s.evidence });
    });

    merged = removedAny ? merged : [...merged];
    const landed = [];
    fresh.forEach((f) => {
      if (merged.length >= MAX_NICE_TO_HAVES) return;
      const master = masterMap.get(f.key) || {};
      merged.push({
        value: f.id,
        label: master.label || f.label,
        tab: f.tab,
        id: f.id,
        Icon: master.Icon,
        Img: master.Img,
        Webp_Img: master.Webp_Img,
      });
      aiOwned.current[f.key] = true;
      landed.push(f);
    });

    if (!removedAny && !landed.length) return;
    merged = merged.map((item, index) => ({ ...item, order: index + 1 }));
    handleFormValues({ nice2Have: merged, focusOut: true });
    if (landed.length) setDismissed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestions, active, masterMap, answered, loading, debouncing]);

  // Heal rows saved without icon fields (drafts from before hydration, or any
  // other inject path): fill only missing fields, once, so this cannot loop.
  useEffect(() => {
    if (!active || masterMap.size === 0) return;
    const rows = Array.isArray(selectedNiceToHave) ? selectedNiceToHave : [];
    let changed = false;
    const healed = rows.map((item) => {
      if (item.Icon != null) return item;
      const master = masterMap.get(rowKey(item.tab, item.value ?? item.id));
      if (!master || master.Icon == null) return item;
      changed = true;
      return {
        ...item,
        Icon: master.Icon,
        Img: item.Img ?? master.Img,
        Webp_Img: item.Webp_Img ?? master.Webp_Img,
      };
    });
    if (changed) handleFormValues({ nice2Have: healed });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterMap, active, selectedNiceToHave]);

  const selected = Array.isArray(selectedNiceToHave) ? selectedNiceToHave : [];
  // Derived from the CURRENT verdict, not from what this mount happened to
  // apply: on a reload or remount the suggestions are already ticked, nothing
  // "lands", and a state-backed list would be empty — the panel would vanish
  // even though the AI had filled the section. Unticking in the selects still
  // clears a chip, because the item is no longer in `selected`.
  const chips = suggestions
    .map((s) => {
      const tab = TAB_MAP[s.tab];
      if (!tab) return null;
      const id = Number(s.id);
      return {
        key: rowKey(tab, id),
        tab,
        id,
        label: s.label,
        evidence: s.evidence,
      };
    })
    .filter(Boolean)
    .filter((c) =>
      selected.some(
        (item) => item.tab === c.tab && Number(item.value ?? item.id) === c.id,
      ),
    );

  const handleRemove = (chip) => {
    const merged = selected
      .filter(
        (item) =>
          !(item.tab === chip.tab && Number(item.value ?? item.id) === chip.id),
      )
      .map((item, index) => ({ ...item, order: index + 1 }));
    handleFormValues({ nice2Have: merged, focusOut: true });
  };

  if (!active) return null;
  if (dismissed) return null;
  // Busy covers the whole thinking window: the debounce while keystrokes
  // settle AND the fetch itself. Without the first half the panel blinked out
  // between typing and the query starting.
  const busy = loading || (textReady && debouncing);
  // Nothing to say yet and nothing on the way: stay out of the form.
  if (!textReady && chips.length === 0 && !busy) {
    return (
      <div className="ai-n2h-strip ai-n2h-hint">
        <div className="ai-n2h-strip-head">
          <span className="ai-n2h-spark" aria-hidden="true">
            ✦
          </span>
          Nice-to-haves fill themselves
          <span className="ai-n2h-badge">AI</span>
        </div>
        <p className="ai-n2h-sub">
          Write what your buyer wants above and the matching features get ticked
          in the Nice-to-haves section automatically. You stay in control and
          can adjust them any time.
        </p>
      </div>
    );
  }
  // A real verdict with nothing found keeps the shell and coaches, instead
  // of vanishing - "I want a house for a family of 4" names no features yet.
  if (!busy && chips.length === 0) {
    if (!answered) return null;
    return (
      <div className="ai-n2h-strip ai-n2h-hint">
        <div className="ai-n2h-strip-head">
          <span className="ai-n2h-spark" aria-hidden="true">
            ✦
          </span>
          No features spotted yet
          <span className="ai-n2h-badge">AI</span>
        </div>
        <p className="ai-n2h-sub">
          Mention specifics in the Description, like a pool, a home office, a
          fenced yard or hardwood floors, and they&apos;ll be ticked in
          Nice-to-haves automatically.
        </p>
      </div>
    );
  }

  // ONE shell from here on. Swapping between separate trees unmounted the card
  // and it visibly blinked on every edit; only the contents change now.
  const heading = busy
    ? chips.length
      ? "Re-reading your description…"
      : "Reading your description…"
    : `${chips.length} ${chips.length === 1 ? "feature" : "features"} ticked from your description`;

  return (
    <div
      className={`ai-n2h-strip${busy ? " ai-n2h-loading" : ""}`}
      role="status"
      aria-live="polite"
    >
      <div className="ai-n2h-strip-head">
        <span className="ai-n2h-spark" aria-hidden="true">
          ✦
        </span>
        {heading}
        <span className="ai-n2h-badge">AI</span>
        {!busy && (
          <button
            type="button"
            className="ai-n2h-dismiss"
            aria-label="Dismiss"
            onClick={() => setDismissed(true)}
          >
            &times;
          </button>
        )}
      </div>
      <p className="ai-n2h-sub">
        {busy
          ? "Matching what you wrote against the feature list…"
          : "We spotted these in what you wrote and ticked them in the Nice-to-haves section. Remove any that don't fit."}
      </p>
      <div className="ai-n2h-chips">
        {chips.length > 0
          ? chips.map((chip) => (
              <span
                key={chip.key}
                className="ai-n2h-chip"
                title={chip.evidence ? `Found: "${chip.evidence}"` : undefined}
              >
                {chip.label}
                {!busy && (
                  <button
                    type="button"
                    className="ai-n2h-chip-remove"
                    aria-label={`Remove ${chip.label}`}
                    onClick={() => handleRemove(chip)}
                  >
                    &times;
                  </button>
                )}
              </span>
            ))
          : // First read: skeletons show the shape of what is coming.
            [72, 104, 88, 120].map((w, i) => (
              <span
                key={`sk-${i}`}
                className="ai-n2h-chip ai-n2h-chip--skeleton"
                style={{ width: w, animationDelay: `${i * 0.12}s` }}
                aria-hidden="true"
              />
            ))}
      </div>
    </div>
  );
};

export default AiNiceToHaveSuggestions;
